/**
 * Progress billing / work-in-progress (WIP) schedule.
 *
 * Pure module — no Firestore or React imports. The Cloud Functions copy in
 * `functions/src/progressBillingDeps.ts` mirrors this file so the CPA tab and
 * the finance agent API report identical numbers.
 *
 * Percent complete uses the cost-to-cost method: direct job cost to date over
 * the proposal budget. Earned revenue is contract value × percent complete.
 * Only under-contract jobs are included (not preconstruction or estimating).
 * Billed to date is the live QuickBooks invoice total. When a customer
 * prepayment shrinks that total, the CRM installment amount is the floor so
 * we still show the deposit that was billed. Stale line items are ignored —
 * they overstate jobs whose invoice was later cut down (Oratory). P&L cash
 * collected is a further floor: finalized jobs often have payments and no
 * matched invoice, and $0 billed vs the contract is wrong.
 */

export type ProgressBillingMethod = 'cost_to_cost';

export type ProgressBillingStatus = 'wip' | 'completed';

export type ProgressBillingReportKind = 'wip_schedule' | 'completed_projects' | 'billing_summary';

/** Same buckets as the Project P&L dashboard groups. */
export type ProgressBillingGroup = 'contracted' | 'preconstruction' | 'estimating';

export const PROGRESS_BILLING_GROUP_ORDER: ProgressBillingGroup[] = ['contracted', 'preconstruction', 'estimating'];

export const PROGRESS_BILLING_GROUP_LABELS: Record<ProgressBillingGroup, string> = {
  contracted: 'Under Contract',
  preconstruction: 'Preconstruction',
  estimating: 'Estimating',
};

/** Progress billing covers under-contract jobs only. */
export function isProgressBillingGroup(group: ProgressBillingGroup | undefined): boolean {
  return (group ?? 'contracted') === 'contracted';
}

/** `contract` = deal value; `collected` = cash received (pre-CRM jobs with no proposal / deal value). */
export type ProgressBillingContractSource = 'contract' | 'collected';

export const PROGRESS_BILLING_COLLECTED_MARK = '*';
export const PROGRESS_BILLING_COLLECTED_NOTE =
  '* Collected revenue — job predates the CRM (no proposal or deal value); contract shown is cash actually received.';

export const PROGRESS_BILLING_REPORT_LABELS: Record<ProgressBillingReportKind, string> = {
  wip_schedule: 'WIP schedule',
  completed_projects: 'Completed projects',
  billing_summary: 'Billing summary',
};

/** Fields read from a Project P&L row (frontend or functions aggregate). */
export interface ProgressBillingPlRow {
  dealId: string;
  dealTitle: string;
  contactName?: string;
  dealValue: number;
  budget: number;
  actualCost: number;
  allocatedOverhead: number;
  cashCollected: number;
  hasProposal?: boolean;
  /** Active ghost P&L adjustments. Applied to contract value only when `hasProposal` is false, unless `plReconciliationContractTotal` is set. */
  plReconciliationTotal?: number;
  /** Ghost dollars to add to the Progress Billing contract. Collected revenue already includes its ghost. */
  plReconciliationContractTotal?: number;
  /** Row revenue when it is not the deal value (see `revenueSource`). */
  revenue?: number;
  revenueSource?: ProgressBillingContractSource;
  /** Project P&L group; defaults to `contracted`. */
  group?: ProgressBillingGroup;
  pmFinalizedAt?: Date | null;
}

/** Fields read from a synced QuickBooks invoice. */
export interface ProgressBillingInvoice {
  matchedDealId?: string;
  quickbooksInvoiceId?: string;
  totalAmount: number;
  balance: number;
  status: string;
  txnDate?: string;
}

/** Fields read from a sales order (base order only; change orders are skipped). */
export interface ProgressBillingSalesOrder {
  id?: string;
  dealId: string;
  isChangeOrder?: boolean;
  billingStyle?: string;
  quickbooksInvoiceId?: string;
  installmentInvoices?: Array<{
    installmentId: string;
    status?: string;
    amount?: number;
    quickbooksInvoiceId?: string;
    /** Set on a structure-group slice. Absent on a whole-milestone invoice. */
    partialId?: string;
    structureIds?: string[];
    /** True on the invoice that finishes the milestone. */
    closesMilestone?: boolean;
  }>;
}

export type ProgressInstallmentStep = 'deposit' | 'pre_shipment' | 'final';

export const PROGRESS_INSTALLMENT_STEPS: ProgressInstallmentStep[] = ['deposit', 'pre_shipment', 'final'];

export const PROGRESS_INSTALLMENT_LABELS: Record<ProgressInstallmentStep, string> = {
  deposit: '50% deposit',
  pre_shipment: '40% pre-shipment',
  final: '10% final',
};

export interface ProgressBillingProject {
  dealId: string;
  label: string;
  clientName: string;
  status: ProgressBillingStatus;
  completedAt: Date | null;
  method: ProgressBillingMethod;
  group: ProgressBillingGroup;

  contractValue: number;
  /** `collected` when contractValue is cash received rather than a signed value. */
  contractValueSource: ProgressBillingContractSource;
  estimatedCost: number;
  costToDate: number;
  costToComplete: number;
  /** 0–1, null when the proposal has no budget. */
  percentComplete: number | null;
  earnedRevenue: number;

  billedToDate: number;
  collectedToDate: number;
  openReceivable: number;
  /** Billings in excess of earned revenue (a liability). */
  overBilled: number;
  /** Earned revenue in excess of billings (an asset). */
  underBilled: number;
  /** Positive when over-billed, negative when under-billed. */
  netOverUnder: number;

  estimatedGrossProfit: number;
  estimatedGrossProfitPct: number | null;

  billingStyle: 'progress' | 'full' | 'unknown';
  billedInstallments: ProgressInstallmentStep[];
  nextInstallment: ProgressInstallmentStep | null;
  invoiceCount: number;
}

export interface ProgressBillingTotals {
  wipCount: number;
  completedCount: number;
  completedThisYearCount: number;
  contractValueInProgress: number;
  costToDate: number;
  earnedRevenue: number;
  billedToDate: number;
  collectedToDate: number;
  overBilled: number;
  underBilled: number;
  netOverUnder: number;
  completedContractValue: number;
  completedGrossProfit: number;
}

const IGNORED_INVOICE_STATUSES = new Set(['voided', 'removed_in_qb', 'credited_for_consolidation']);

function money(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function round(value: number): number {
  return Math.round(value);
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const maybe = value as { toDate?: () => Date };
  if (typeof maybe.toDate === 'function') return maybe.toDate();
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Invoices that still count toward billings (voided / removed rows do not). */
export function invoiceCountsForBilling(inv: ProgressBillingInvoice): boolean {
  return !IGNORED_INVOICE_STATUSES.has(inv.status);
}

/** Sales-order installment rows use the same statuses. A deleted QuickBooks invoice stays on the contract as removed_in_qb and must not add to billed-to-date. */
function installmentCountsTowardBilling(status: string | undefined): boolean {
  return !status || !IGNORED_INVOICE_STATUSES.has(status);
}

/**
 * Live QuickBooks invoice total. Do not substitute stale line items: an
 * invoice edited down (full → 50% deposit) keeps old lines in the mirror.
 */
export function invoiceBilledAmount(inv: ProgressBillingInvoice): number {
  return money(inv.totalAmount);
}

/** Paid/open installment amounts — floor when QB TotalAmt is net of a prepayment. */
export function salesOrderBilledAmount(so: ProgressBillingSalesOrder | undefined): number {
  if (!so) return 0;
  return (so.installmentInvoices ?? []).reduce((sum, entry) => {
    if (!entry || !installmentCountsTowardBilling(entry.status)) return sum;
    return sum + money(entry.amount);
  }, 0);
}

/**
 * Billings for a job. Each progress installment uses the greater of the CRM
 * installment amount and its QuickBooks total (prepayment-netted invoices).
 * Other invoices (change orders) add on top. Stale line items are never used.
 */
export function progressBilledToDate(
  invoices: ProgressBillingInvoice[],
  so: ProgressBillingSalesOrder | undefined,
): number {
  const installments = (so?.installmentInvoices ?? []).filter(
    entry => entry && installmentCountsTowardBilling(entry.status) && money(entry.amount) > 0,
  );
  if (installments.length === 0) {
    return round(invoices.reduce((sum, inv) => sum + invoiceBilledAmount(inv), 0));
  }
  const usedIds = new Set(
    installments.map(entry => String(entry.quickbooksInvoiceId || '')).filter(Boolean),
  );
  let billed = 0;
  for (const entry of installments) {
    const qbId = String(entry.quickbooksInvoiceId || '');
    const match = qbId ? invoices.find(inv => String(inv.quickbooksInvoiceId || '') === qbId) : undefined;
    billed += Math.max(money(entry.amount), match ? invoiceBilledAmount(match) : 0);
  }
  for (const inv of invoices) {
    const id = String(inv.quickbooksInvoiceId || '');
    if (!id || usedIds.has(id)) continue;
    billed += invoiceBilledAmount(inv);
  }
  return round(billed);
}

/** Invoice posted on or before the as-of date (no date → counts). */
function invoiceOnOrBefore(inv: ProgressBillingInvoice, asOf: Date | undefined): boolean {
  if (!asOf || !inv.txnDate) return true;
  const d = toDate(inv.txnDate);
  return !d || d.getTime() <= asOf.getTime();
}

function legacyDepositSatisfied(so: ProgressBillingSalesOrder): boolean {
  // An explicit lump-sum order's header invoice is the full invoice, not a deposit.
  if (!so.quickbooksInvoiceId || so.billingStyle === 'full') return false;
  const billed = new Set(
    (so.installmentInvoices ?? []).filter((e) => e.status !== 'voided').map((e) => e.installmentId),
  );
  return !billed.has('deposit');
}

/**
 * A whole-milestone invoice, or the last slice. A partial that leaves money
 * unbilled does not finish the step, so Next installment stays on it.
 */
function installmentInvoiceClosesStep(entry: {
  status?: string;
  partialId?: string;
  structureIds?: string[];
  closesMilestone?: boolean;
} | null | undefined): boolean {
  if (!entry || !installmentCountsTowardBilling(entry.status)) return false;
  if (entry.closesMilestone === true) return true;
  if (entry.partialId) return false;
  if (entry.structureIds && entry.structureIds.length > 0) return false;
  return true;
}

function billedInstallmentSteps(so: ProgressBillingSalesOrder): ProgressInstallmentStep[] {
  const ids = new Set<string>();
  for (const entry of so.installmentInvoices ?? []) {
    if (installmentInvoiceClosesStep(entry)) ids.add(entry.installmentId);
  }
  if (legacyDepositSatisfied(so)) ids.add('deposit');
  return PROGRESS_INSTALLMENT_STEPS.filter((step) => ids.has(step));
}

function billingStyleFor(so: ProgressBillingSalesOrder | undefined): ProgressBillingProject['billingStyle'] {
  if (!so) return 'unknown';
  if (so.billingStyle === 'progress' || legacyDepositSatisfied(so)) return 'progress';
  if (so.billingStyle === 'full' || so.quickbooksInvoiceId) return 'full';
  return 'unknown';
}

export interface BuildProgressBillingProjectsInput {
  plRows: ProgressBillingPlRow[];
  qbInvoices: ProgressBillingInvoice[];
  salesOrders?: ProgressBillingSalesOrder[];
  /** Period end; invoices dated after it are ignored. Defaults to now. */
  asOf?: Date;
}

export function buildProgressBillingProjects(input: BuildProgressBillingProjectsInput): ProgressBillingProject[] {
  const asOf = input.asOf;
  const invoicesByDeal = new Map<string, ProgressBillingInvoice[]>();
  for (const inv of input.qbInvoices) {
    const dealId = (inv.matchedDealId || '').trim();
    if (!dealId || !invoiceCountsForBilling(inv) || !invoiceOnOrBefore(inv, asOf)) continue;
    const list = invoicesByDeal.get(dealId) ?? [];
    list.push(inv);
    invoicesByDeal.set(dealId, list);
  }
  const baseOrderByDeal = new Map<string, ProgressBillingSalesOrder>();
  for (const so of input.salesOrders ?? []) {
    if (so.isChangeOrder || !so.dealId) continue;
    if (!baseOrderByDeal.has(so.dealId)) baseOrderByDeal.set(so.dealId, so);
  }

  return input.plRows.filter(row => isProgressBillingGroup(row.group)).map((row) => {
    const contractValueSource: ProgressBillingContractSource =
      row.revenueSource === 'collected' ? 'collected' : 'contract';
    const baseContract = contractValueSource === 'collected' ? money(row.revenue) : money(row.dealValue);
    // Collected revenue already includes its ghost. Otherwise add the contract ghost:
    // every ghost on a job with no proposal, or a flagged ghost on a job that has one.
    const contractGhost = row.plReconciliationContractTotal != null
      ? row.plReconciliationContractTotal
      : row.hasProposal === false
        ? row.plReconciliationTotal
        : 0;
    const ghostOnProjectValue = contractValueSource === 'collected' ? 0 : money(contractGhost);
    const contractValue = round(baseContract + ghostOnProjectValue);
    const estimatedCost = round(money(row.budget));
    const costToDate = round(Math.max(0, money(row.actualCost) - money(row.allocatedOverhead)));
    const completedAt = toDate(row.pmFinalizedAt);
    const status: ProgressBillingStatus = completedAt ? 'completed' : 'wip';

    let percentComplete: number | null = null;
    if (status === 'completed') percentComplete = 1;
    else if (estimatedCost > 0) percentComplete = Math.min(1, costToDate / estimatedCost);

    const earnedRevenue = percentComplete == null ? 0 : round(contractValue * percentComplete);
    const invoices = invoicesByDeal.get(row.dealId) ?? [];
    const so = baseOrderByDeal.get(row.dealId);
    const invoiced = progressBilledToDate(invoices, so);
    const cash = round(money(row.cashCollected));
    const billedToDate = Math.max(invoiced, cash);
    const openReceivable = round(invoices.reduce((sum, inv) => sum + Math.max(0, money(inv.balance)), 0));
    const collectedToDate = Math.max(cash, Math.max(0, invoiced - openReceivable));

    const overBilled = percentComplete == null ? 0 : Math.max(0, billedToDate - earnedRevenue);
    const underBilled = percentComplete == null ? 0 : Math.max(0, earnedRevenue - billedToDate);
    const costToComplete = Math.max(0, estimatedCost - costToDate);
    const estimatedGrossProfit = contractValue - (status === 'completed' ? costToDate : Math.max(estimatedCost, costToDate));
    const estimatedGrossProfitPct = contractValue > 0 ? estimatedGrossProfit / contractValue : null;

    const billedInstallments = so ? billedInstallmentSteps(so) : [];
    const billingStyle = billingStyleFor(so);
    const nextInstallment =
      billingStyle === 'progress'
        ? PROGRESS_INSTALLMENT_STEPS.find((step) => !billedInstallments.includes(step)) ?? null
        : null;

    return {
      dealId: row.dealId,
      label: row.dealTitle || row.dealId,
      clientName: row.contactName || '',
      status,
      completedAt,
      method: 'cost_to_cost',
      group: row.group ?? 'contracted',
      contractValue,
      contractValueSource,
      estimatedCost,
      costToDate,
      costToComplete,
      percentComplete,
      earnedRevenue,
      billedToDate,
      collectedToDate,
      openReceivable,
      overBilled,
      underBilled,
      netOverUnder: overBilled - underBilled,
      estimatedGrossProfit,
      estimatedGrossProfitPct,
      billingStyle,
      billedInstallments,
      nextInstallment,
      invoiceCount: invoices.length,
    };
  });
}

export function summarizeProgressBilling(
  projects: ProgressBillingProject[],
  asOf: Date = new Date(),
): ProgressBillingTotals {
  const totals: ProgressBillingTotals = {
    wipCount: 0,
    completedCount: 0,
    completedThisYearCount: 0,
    contractValueInProgress: 0,
    costToDate: 0,
    earnedRevenue: 0,
    billedToDate: 0,
    collectedToDate: 0,
    overBilled: 0,
    underBilled: 0,
    netOverUnder: 0,
    completedContractValue: 0,
    completedGrossProfit: 0,
  };
  const year = asOf.getFullYear();
  for (const p of projects) {
    if (p.status === 'completed') {
      totals.completedCount += 1;
      if (p.completedAt && p.completedAt.getFullYear() === year) totals.completedThisYearCount += 1;
      totals.completedContractValue += p.contractValue;
      totals.completedGrossProfit += p.estimatedGrossProfit;
      continue;
    }
    totals.wipCount += 1;
    totals.contractValueInProgress += p.contractValue;
    totals.costToDate += p.costToDate;
    totals.earnedRevenue += p.earnedRevenue;
    totals.billedToDate += p.billedToDate;
    totals.collectedToDate += p.collectedToDate;
    totals.overBilled += p.overBilled;
    totals.underBilled += p.underBilled;
  }
  totals.netOverUnder = totals.overBilled - totals.underBilled;
  return totals;
}

export interface ProgressBillingReportColumn {
  field: string;
  label: string;
  type: 'string' | 'currency' | 'percentage' | 'date' | 'number';
}

export interface ProgressBillingReport {
  kind: ProgressBillingReportKind;
  title: string;
  asOf: Date;
  columns: ProgressBillingReportColumn[];
  rows: Array<Record<string, string | number | Date | null>>;
  totals: Record<string, number>;
}

function pct(value: number | null): number | null {
  return value == null ? null : Math.round(value * 1000) / 10;
}

/** Jobs below this contract value are one combined line on the WIP and Completed reports. */
export const PROGRESS_BILLING_SMALL_CONTRACT = 10_000;

function byContractDesc(a: ProgressBillingProject, b: ProgressBillingProject): number {
  return b.contractValue - a.contractValue || a.label.localeCompare(b.label);
}

function sortByContract(projects: ProgressBillingProject[]): ProgressBillingProject[] {
  return projects.slice().sort(byContractDesc);
}

export interface ProgressBillingSmallRollup {
  count: number;
  label: string;
  clientName: string;
  contractValue: number;
  estimatedCost: number;
  costToDate: number;
  /** 0–1, null when none of the jobs have a budget. */
  percentComplete: number | null;
  earnedRevenue: number;
  billedToDate: number;
  collectedToDate: number;
  overBilled: number;
  underBilled: number;
  netOverUnder: number;
  estimatedGrossProfit: number;
  estimatedGrossProfitPct: number | null;
}

/** Largest contracts first. Anything under $10,000 is returned separately for one rollup line. */
export function partitionSmallContracts(projects: ProgressBillingProject[]): {
  listed: ProgressBillingProject[];
  small: ProgressBillingProject[];
} {
  const listed: ProgressBillingProject[] = [];
  const small: ProgressBillingProject[] = [];
  for (const project of projects) {
    if (project.contractValue < PROGRESS_BILLING_SMALL_CONTRACT) small.push(project);
    else listed.push(project);
  }
  listed.sort(byContractDesc);
  return { listed, small };
}

function sumProjects(projects: ProgressBillingProject[], pick: (project: ProgressBillingProject) => number): number {
  return projects.reduce((sum, project) => sum + pick(project), 0);
}

export function rollupSmallContracts(projects: ProgressBillingProject[]): ProgressBillingSmallRollup | null {
  if (!projects.length) return null;
  const contractValue = sumProjects(projects, (project) => project.contractValue);
  const estimatedCost = sumProjects(projects, (project) => project.estimatedCost);
  const costToDate = sumProjects(projects, (project) => project.costToDate);
  const earnedRevenue = sumProjects(projects, (project) => project.earnedRevenue);
  const billedToDate = sumProjects(projects, (project) => project.billedToDate);
  const collectedToDate = sumProjects(projects, (project) => project.collectedToDate);
  const overBilled = sumProjects(projects, (project) => project.overBilled);
  const underBilled = sumProjects(projects, (project) => project.underBilled);
  const estimatedGrossProfit = sumProjects(projects, (project) => project.estimatedGrossProfit);
  const anyPercent = projects.some((project) => project.percentComplete != null);
  return {
    count: projects.length,
    label: 'Projects under $10,000',
    clientName: projects.length === 1 ? '1 job' : `${projects.length} jobs`,
    contractValue,
    estimatedCost,
    costToDate,
    percentComplete: !anyPercent || estimatedCost <= 0 ? null : Math.min(1, costToDate / estimatedCost),
    earnedRevenue,
    billedToDate,
    collectedToDate,
    overBilled,
    underBilled,
    netOverUnder: overBilled - underBilled,
    estimatedGrossProfit,
    estimatedGrossProfitPct: contractValue > 0 ? estimatedGrossProfit / contractValue : null,
  };
}

/** Under Contract first, then Preconstruction, then Estimating; contract value desc within each. */
export function sortByGroupThenContract(projects: ProgressBillingProject[]): ProgressBillingProject[] {
  const rank = (g: ProgressBillingGroup) => PROGRESS_BILLING_GROUP_ORDER.indexOf(g);
  return projects
    .slice()
    .sort((a, b) => rank(a.group) - rank(b.group) || b.contractValue - a.contractValue || a.label.localeCompare(b.label));
}

export function groupProgressBillingProjects(
  projects: ProgressBillingProject[],
): Array<{ group: ProgressBillingGroup; label: string; projects: ProgressBillingProject[] }> {
  return PROGRESS_BILLING_GROUP_ORDER.map((group) => ({
    group,
    label: PROGRESS_BILLING_GROUP_LABELS[group],
    projects: sortByContract(projects.filter((p) => p.group === group)),
  })).filter((g) => g.projects.length > 0);
}

/** Project label for reports; collected-revenue jobs carry the `*` marker. */
export function reportProjectLabel(p: ProgressBillingProject): string {
  return p.contractValueSource === 'collected' ? `${p.label} ${PROGRESS_BILLING_COLLECTED_MARK}` : p.label;
}

export function buildProgressBillingReport(
  kind: ProgressBillingReportKind,
  projects: ProgressBillingProject[],
  asOf: Date = new Date(),
): ProgressBillingReport {
  const wip = sortByGroupThenContract(projects.filter((p) => p.status === 'wip'));
  const completed = projects
    .filter((p) => p.status === 'completed')
    .slice()
    .sort((a, b) => (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0));
  const sum = (rows: ProgressBillingProject[], pick: (p: ProgressBillingProject) => number) =>
    rows.reduce((s, p) => s + pick(p), 0);

  if (kind === 'wip_schedule') {
    const openJobs = projects.filter((p) => p.status === 'wip');
    const { listed, small } = partitionSmallContracts(openJobs);
    const smallRollup = rollupSmallContracts(small);
    return {
      kind,
      title: 'Work in Progress Schedule',
      asOf,
      columns: [
        { field: 'group', label: 'Group', type: 'string' },
        { field: 'project', label: 'Project', type: 'string' },
        { field: 'client', label: 'Client', type: 'string' },
        { field: 'contractValue', label: 'Contract', type: 'currency' },
        { field: 'estimatedCost', label: 'Est. cost', type: 'currency' },
        { field: 'costToDate', label: 'Cost to date', type: 'currency' },
        { field: 'percentComplete', label: '% complete', type: 'percentage' },
        { field: 'earnedRevenue', label: 'Earned revenue', type: 'currency' },
        { field: 'billedToDate', label: 'Billed to date', type: 'currency' },
        { field: 'overBilled', label: 'Over-billed', type: 'currency' },
        { field: 'underBilled', label: 'Under-billed', type: 'currency' },
        { field: 'estimatedGrossProfit', label: 'Est. gross profit', type: 'currency' },
      ],
      rows: [
        ...listed.map((p) => ({
          group: PROGRESS_BILLING_GROUP_LABELS[p.group],
          project: reportProjectLabel(p),
          client: p.clientName,
          contractValue: p.contractValue,
          estimatedCost: p.estimatedCost,
          costToDate: p.costToDate,
          percentComplete: pct(p.percentComplete),
          earnedRevenue: p.earnedRevenue,
          billedToDate: p.billedToDate,
          overBilled: p.overBilled,
          underBilled: p.underBilled,
          estimatedGrossProfit: p.estimatedGrossProfit,
        })),
        ...(smallRollup ? [{
          group: '—',
          project: smallRollup.label,
          client: smallRollup.clientName,
          contractValue: smallRollup.contractValue,
          estimatedCost: smallRollup.estimatedCost,
          costToDate: smallRollup.costToDate,
          percentComplete: pct(smallRollup.percentComplete),
          earnedRevenue: smallRollup.earnedRevenue,
          billedToDate: smallRollup.billedToDate,
          overBilled: smallRollup.overBilled,
          underBilled: smallRollup.underBilled,
          estimatedGrossProfit: smallRollup.estimatedGrossProfit,
        }] : []),
      ],
      totals: {
        contractValue: sum(openJobs, (p) => p.contractValue),
        estimatedCost: sum(openJobs, (p) => p.estimatedCost),
        costToDate: sum(openJobs, (p) => p.costToDate),
        earnedRevenue: sum(openJobs, (p) => p.earnedRevenue),
        billedToDate: sum(openJobs, (p) => p.billedToDate),
        overBilled: sum(openJobs, (p) => p.overBilled),
        underBilled: sum(openJobs, (p) => p.underBilled),
        estimatedGrossProfit: sum(openJobs, (p) => p.estimatedGrossProfit),
      },
    };
  }

  if (kind === 'completed_projects') {
    const doneJobs = projects.filter((p) => p.status === 'completed');
    const { listed, small } = partitionSmallContracts(doneJobs);
    const smallRollup = rollupSmallContracts(small);
    return {
      kind,
      title: 'Completed Projects',
      asOf,
      columns: [
        { field: 'project', label: 'Project', type: 'string' },
        { field: 'client', label: 'Client', type: 'string' },
        { field: 'completedAt', label: 'Completed', type: 'date' },
        { field: 'contractValue', label: 'Contract', type: 'currency' },
        { field: 'costToDate', label: 'Final cost', type: 'currency' },
        { field: 'estimatedGrossProfit', label: 'Gross profit', type: 'currency' },
        { field: 'estimatedGrossProfitPct', label: 'GP %', type: 'percentage' },
        { field: 'billedToDate', label: 'Billed', type: 'currency' },
        { field: 'collectedToDate', label: 'Collected', type: 'currency' },
      ],
      rows: [
        ...listed.map((p) => ({
          project: reportProjectLabel(p),
          client: p.clientName,
          completedAt: p.completedAt,
          contractValue: p.contractValue,
          costToDate: p.costToDate,
          estimatedGrossProfit: p.estimatedGrossProfit,
          estimatedGrossProfitPct: pct(p.estimatedGrossProfitPct),
          billedToDate: p.billedToDate,
          collectedToDate: p.collectedToDate,
        })),
        ...(smallRollup ? [{
          project: smallRollup.label,
          client: smallRollup.clientName,
          completedAt: null,
          contractValue: smallRollup.contractValue,
          costToDate: smallRollup.costToDate,
          estimatedGrossProfit: smallRollup.estimatedGrossProfit,
          estimatedGrossProfitPct: pct(smallRollup.estimatedGrossProfitPct),
          billedToDate: smallRollup.billedToDate,
          collectedToDate: smallRollup.collectedToDate,
        }] : []),
      ],
      totals: {
        contractValue: sum(doneJobs, (p) => p.contractValue),
        costToDate: sum(doneJobs, (p) => p.costToDate),
        estimatedGrossProfit: sum(doneJobs, (p) => p.estimatedGrossProfit),
        billedToDate: sum(doneJobs, (p) => p.billedToDate),
        collectedToDate: sum(doneJobs, (p) => p.collectedToDate),
      },
    };
  }

  const all = [...wip, ...completed];
  return {
    kind,
    title: 'Progress Billing Summary',
    asOf,
    columns: [
      { field: 'group', label: 'Group', type: 'string' },
      { field: 'project', label: 'Project', type: 'string' },
      { field: 'status', label: 'Status', type: 'string' },
      { field: 'billingStyle', label: 'Billing', type: 'string' },
      { field: 'contractValue', label: 'Contract', type: 'currency' },
      { field: 'billedToDate', label: 'Billed', type: 'currency' },
      { field: 'collectedToDate', label: 'Collected', type: 'currency' },
      { field: 'openReceivable', label: 'Open A/R', type: 'currency' },
      { field: 'remainingToBill', label: 'Remaining to bill', type: 'currency' },
      { field: 'nextInstallment', label: 'Next installment', type: 'string' },
    ],
    rows: all.map((p) => ({
      group: PROGRESS_BILLING_GROUP_LABELS[p.group],
      project: reportProjectLabel(p),
      status: p.status === 'wip' ? 'In progress' : 'Completed',
      billingStyle: p.billingStyle === 'progress' ? 'Progress' : p.billingStyle === 'full' ? 'Lump sum' : '—',
      contractValue: p.contractValue,
      billedToDate: p.billedToDate,
      collectedToDate: p.collectedToDate,
      openReceivable: p.openReceivable,
      remainingToBill: Math.max(0, p.contractValue - p.billedToDate),
      nextInstallment: p.nextInstallment ? PROGRESS_INSTALLMENT_LABELS[p.nextInstallment] : '—',
    })),
    totals: {
      contractValue: sum(all, (p) => p.contractValue),
      billedToDate: sum(all, (p) => p.billedToDate),
      collectedToDate: sum(all, (p) => p.collectedToDate),
      openReceivable: sum(all, (p) => p.openReceivable),
      remainingToBill: sum(all, (p) => Math.max(0, p.contractValue - p.billedToDate)),
    },
  };
}
